import { PromptTemplate } from '../types/prompt';

export const promptTemplates: PromptTemplate[] = [
  // Midjourney Templates
  {
    id: 'mj-portrait',
    name: 'Cinematic Portrait',
    category: 'image',
    platform: 'midjourney',
    template: '{subject} portrait, {lighting} lighting, {mood} mood, {style} style, {camera} shot --ar {aspect_ratio} --v 7 --style raw',
    variables: ['subject', 'lighting', 'mood', 'style', 'camera', 'aspect_ratio'],
    parameters: {
      aspect_ratio: '2:3',
      version: '7'
    }
  },
  {
    id: 'mj-landscape',
    name: 'Epic Landscape',
    category: 'image',
    platform: 'midjourney',
    template: '{location} landscape, {time_of_day}, {weather}, {style} art style, dramatic composition, hyperrealistic details --ar {aspect_ratio} --v 7 --q 2 --style raw',
    variables: ['location', 'time_of_day', 'weather', 'style', 'aspect_ratio'],
    parameters: {
      aspect_ratio: '16:9',
      quality: '2'
    }
  },
  {
    id: 'mj-fantasy',
    name: 'Fantasy Scene',
    category: 'image',
    platform: 'midjourney',
    template: '{creature} in {environment}, {magic_type} magic effects, {art_style} art style, epic fantasy atmosphere, highly detailed --ar {aspect_ratio} --v 7 --stylize {stylize} --chaos 20',
    variables: ['creature', 'environment', 'magic_type', 'art_style', 'aspect_ratio', 'stylize'],
    parameters: {
      aspect_ratio: '16:9',
      stylize: '750'
    }
  },
  {
    id: 'mj-architecture',
    name: 'Architecture',
    category: 'image',
    platform: 'midjourney',
    template: '{building_type} architecture, {architectural_style} style, {materials}, {lighting} lighting, {perspective} view, photorealistic render --ar {aspect_ratio} --v 7 --style raw --q 2',
    variables: ['building_type', 'architectural_style', 'materials', 'lighting', 'perspective', 'aspect_ratio'],
    parameters: {
      aspect_ratio: '3:2'
    }
  },
  // Google ImageFX Templates
  {
    id: 'google-abstract',
    name: 'Abstract Art',
    category: 'image',
    platform: 'google-imagefx',
    template: 'Abstract {colors} composition with {shapes}, {texture} texture, {mood} feeling, high resolution digital art',
    variables: ['colors', 'shapes', 'texture', 'mood']
  },
  {
    id: 'google-realistic',
    name: 'Photorealistic',
    category: 'image',
    platform: 'google-imagefx',
    template: 'Photorealistic {subject}, {environment} setting, {lighting} lighting, professional photography, 8K resolution',
    variables: ['subject', 'environment', 'lighting']
  },
  {
    id: 'google-illustration',
    name: 'Digital Illustration',
    category: 'image',
    platform: 'google-imagefx',
    template: '{character} character, {art_style} illustration style, {color_palette} color palette, {background} background, detailed artwork',
    variables: ['character', 'art_style', 'color_palette', 'background']
  },
  // Veo Video Templates
  {
    id: 'veo-action',
    name: 'Action Sequence',
    category: 'video',
    platform: 'veo',
    template: '{character} {action} in {location}, {camera_movement} camera, {duration} seconds, {style} cinematography',
    variables: ['character', 'action', 'location', 'camera_movement', 'duration', 'style']
  },
  {
    id: 'veo-timelapse',
    name: 'Timelapse',
    category: 'video',
    platform: 'veo',
    template: 'Timelapse of {subject}, {time_period} duration, {speed}x speed, {environment} setting, {mood} atmosphere',
    variables: ['subject', 'time_period', 'speed', 'environment', 'mood']
  },
  {
    id: 'veo-transition',
    name: 'Scene Transition',
    category: 'video',
    platform: 'veo',
    template: 'Transition from {scene1} to {scene2}, {transition_type} effect, {duration} seconds, {style} visual style',
    variables: ['scene1', 'scene2', 'transition_type', 'duration', 'style']
  },
  // Universal Templates
  {
    id: 'universal-product',
    name: 'Product Shot',
    category: 'image',
    platform: 'universal',
    template: '{product} on {background}, {lighting} lighting, professional product photography, {angle} angle, high detail',
    variables: ['product', 'background', 'lighting', 'angle']
  },
  {
    id: 'universal-concept',
    name: 'Concept Art',
    category: 'image',
    platform: 'universal',
    template: '{concept} concept art, {art_style} style, {color_mood} color mood, {detail_level} detail, professional digital art',
    variables: ['concept', 'art_style', 'color_mood', 'detail_level']
  },
  {
    id: 'universal-character',
    name: 'Character Design',
    category: 'image',
    platform: 'universal',
    template: '{character_type} character, {pose} pose, {clothing} clothing, {art_style} art style, character sheet, detailed design',
    variables: ['character_type', 'pose', 'clothing', 'art_style']
  },
  // New Midjourney v7 Templates with latest best practices
  {
    id: 'mj-v7-photorealistic',
    name: 'MJ v7 Photorealistic',
    category: 'image',
    platform: 'midjourney',
    template: 'Photorealistic {subject}, shot on {camera} with {lens}, {lighting} lighting, {mood} atmosphere, hyperdetailed, professional photography --ar {aspect_ratio} --v 7 --style raw --q 2',
    variables: ['subject', 'camera', 'lens', 'lighting', 'mood', 'aspect_ratio'],
    parameters: {
      aspect_ratio: '3:2',
      version: '7'
    }
  },
  {
    id: 'mj-v7-artistic',
    name: 'MJ v7 Artistic Render',
    category: 'image',
    platform: 'midjourney',
    template: '{subject} in the style of {artist}, {color_palette} color palette, {technique} technique, {mood} mood, masterpiece quality --ar {aspect_ratio} --v 7 --stylize {stylize} --chaos {chaos}',
    variables: ['subject', 'artist', 'color_palette', 'technique', 'mood', 'aspect_ratio', 'stylize', 'chaos'],
    parameters: {
      aspect_ratio: '1:1',
      stylize: '500',
      chaos: '10',
      version: '7'
    }
  },
  {
    id: 'mj-v7-cinematic',
    name: 'MJ v7 Cinematic Scene',
    category: 'image',
    platform: 'midjourney',
    template: 'Cinematic still from {genre} film, {scene}, {cinematography} cinematography, {color_grading} color grading, directed by {director}, anamorphic lens --ar 21:9 --v 7 --style raw --weird {weird}',
    variables: ['genre', 'scene', 'cinematography', 'color_grading', 'director', 'weird'],
    parameters: {
      aspect_ratio: '21:9',
      weird: '100',
      version: '7'
    }
  },
  // DALL-E 3 Latest Templates
  {
    id: 'dalle3-hd',
    name: 'DALL-E 3 HD Quality',
    category: 'image',
    platform: 'dalle3',
    template: 'Create a high-definition image of {subject} with {style} style, featuring {elements}, {lighting} lighting, {mood} atmosphere, ultra detailed, 4K resolution',
    variables: ['subject', 'style', 'elements', 'lighting', 'mood']
  },
  // Google Veo Latest Templates
  {
    id: 'veo-cinematic-v2',
    name: 'Veo Cinematic V2',
    category: 'video',
    platform: 'veo',
    template: 'Cinematic video: {scene}, {camera_movement} camera movement, {fps} fps, {duration} seconds, {style} cinematography style, 4K resolution, professional color grading',
    variables: ['scene', 'camera_movement', 'fps', 'duration', 'style'],
    parameters: {
      fps: '24',
      duration: '10'
    }
  },
  // Stable Diffusion XL Latest
  {
    id: 'sdxl-v2',
    name: 'SDXL High Quality',
    category: 'image',
    platform: 'stable-diffusion-xl',
    template: '{subject}, {style} style, {quality_tags}, {lighting}, {composition}, steps:30, CFG:7, negative prompt: {negative}',
    variables: ['subject', 'style', 'quality_tags', 'lighting', 'composition', 'negative'],
    parameters: {
      quality_tags: 'masterpiece, best quality, ultra detailed',
      negative: 'low quality, blurry, watermark'
    }
  },
  // Midjourney Video Templates (for animated/motion concepts)
  {
    id: 'mj-video-motion',
    name: 'MJ Motion Concept',
    category: 'video',
    platform: 'midjourney-video',
    template: '{action} motion sequence, {subject}, {style} animation style, {fps} fps, smooth motion blur, dynamic movement, cinematic flow --ar {aspect_ratio} --video --motion {motion_value}',
    variables: ['action', 'subject', 'style', 'fps', 'aspect_ratio', 'motion_value'],
    parameters: {
      aspect_ratio: '16:9',
      fps: '24',
      motion_value: '180'
    }
  },
  {
    id: 'mj-video-loop',
    name: 'MJ Seamless Loop',
    category: 'video',
    platform: 'midjourney-video',
    template: 'Seamless loop animation of {subject}, {duration} second cycle, {style} style, perfect loop, smooth transition, {mood} mood --ar {aspect_ratio} --video --fps {fps}',
    variables: ['subject', 'duration', 'style', 'mood', 'aspect_ratio', 'fps'],
    parameters: {
      aspect_ratio: '1:1',
      fps: '30',
      duration: '3'
    }
  },
  {
    id: 'mj-video-cinematic',
    name: 'MJ Cinematic Sequence',
    category: 'video',
    platform: 'midjourney-video',
    template: 'Cinematic sequence: {scene}, {camera_movement} camera, {lighting} lighting, {color_grade} color grade, filmic motion, {duration}s --ar 21:9 --video --style raw',
    variables: ['scene', 'camera_movement', 'lighting', 'color_grade', 'duration'],
    parameters: {
      duration: '5'
    }
  },
  // Kling AI Video Templates
  {
    id: 'kling-realistic',
    name: 'Kling Realistic Video',
    category: 'video',
    platform: 'kling-ai',
    template: 'Create a {duration}-second realistic video: {scene_description}, {camera_angle} angle, {camera_movement} movement, {lighting} lighting, photorealistic quality, 4K resolution, professional cinematography',
    variables: ['duration', 'scene_description', 'camera_angle', 'camera_movement', 'lighting'],
    parameters: {
      duration: '5'
    }
  },
  {
    id: 'kling-animation',
    name: 'Kling Animation',
    category: 'video',
    platform: 'kling-ai',
    template: 'Animated video: {character} performing {action}, {animation_style} animation style, {background}, {duration} seconds, smooth motion, {fps} fps, vibrant colors',
    variables: ['character', 'action', 'animation_style', 'background', 'duration', 'fps'],
    parameters: {
      duration: '5',
      fps: '24'
    }
  },
  {
    id: 'kling-product',
    name: 'Kling Product Showcase',
    category: 'video',
    platform: 'kling-ai',
    template: 'Product video of {product}, {camera_movement} camera movement, {lighting} lighting, {background}, {duration} seconds, professional quality, highlight {features}, commercial style',
    variables: ['product', 'camera_movement', 'lighting', 'background', 'duration', 'features'],
    parameters: {
      duration: '10'
    }
  },
  {
    id: 'kling-storytelling',
    name: 'Kling Narrative Video',
    category: 'video',
    platform: 'kling-ai',
    template: 'Story video: {narrative}, {visual_style} visual style, {pacing} pacing, {mood} mood, {duration} seconds, cinematic transitions, emotional depth, {color_palette} color palette',
    variables: ['narrative', 'visual_style', 'pacing', 'mood', 'duration', 'color_palette'],
    parameters: {
      duration: '15',
      pacing: 'medium'
    }
  },
  {
    id: 'kling-music-video',
    name: 'Kling Music Video',
    category: 'video',
    platform: 'kling-ai',
    template: 'Music video style: {genre} music, {visual_theme}, {editing_style} editing, {effects}, beat-synced motion, {duration} seconds, {color_grading} color grading, dynamic transitions',
    variables: ['genre', 'visual_theme', 'editing_style', 'effects', 'duration', 'color_grading'],
    parameters: {
      duration: '30'
    }
  },
  {
    id: 'kling-vfx',
    name: 'Kling VFX Shot',
    category: 'video',
    platform: 'kling-ai',
    template: 'VFX shot: {effect_type} effect, {scene}, {integration} integration, {duration} seconds, photorealistic rendering, {particle_effects}, cinematic quality, seamless compositing',
    variables: ['effect_type', 'scene', 'integration', 'duration', 'particle_effects'],
    parameters: {
      duration: '3'
    }
  }
];

export const stylePresets = {
  // Art Styles
  artistic: ['oil painting', 'watercolor', 'digital art', 'pencil sketch', 'anime', 'photorealistic'],
  style: ['cyberpunk', 'steampunk', 'minimalist', 'baroque', 'art nouveau', 'surreal'],
  art_style: ['concept art', 'matte painting', 'cel shaded', 'low poly', 'pixel art', 'vector art'],
  
  // Lighting Options
  lighting: ['natural', 'studio', 'dramatic', 'soft', 'neon', 'golden hour', 'rim lighting', 'backlighting'],
  
  // Mood & Atmosphere
  mood: ['peaceful', 'energetic', 'mysterious', 'romantic', 'dark', 'vibrant', 'melancholic', 'ethereal'],
  
  // Camera & Perspectives
  camera: ['close-up', 'wide angle', 'aerial', 'macro', 'portrait', 'establishing shot'],
  perspective: ['eye level', 'bird\'s eye', 'worm\'s eye', 'dutch angle', 'isometric', 'three-quarter view'],
  camera_movement: ['tracking', 'panning', 'dolly zoom', 'handheld', 'steadicam', 'drone shot'],
  
  // Colors & Palettes
  colors: ['vibrant rainbow', 'monochromatic blue', 'pastel', 'neon', 'earth tones', 'black and white'],
  color_palette: ['warm tones', 'cool tones', 'complementary', 'analogous', 'triadic', 'split-complementary'],
  color_mood: ['vibrant', 'muted', 'desaturated', 'high contrast', 'soft pastels', 'deep rich colors'],
  
  // Environments & Settings
  environment: ['forest', 'urban city', 'underwater', 'space', 'desert', 'mountains', 'cyberpunk city'],
  location: ['Tokyo streets', 'New York skyline', 'ancient temple', 'futuristic lab', 'enchanted forest', 'abandoned warehouse'],
  background: ['gradient', 'solid color', 'abstract pattern', 'blurred bokeh', 'studio backdrop', 'natural outdoor'],
  
  // Time & Weather
  time_of_day: ['sunrise', 'morning', 'noon', 'golden hour', 'sunset', 'blue hour', 'night', 'midnight'],
  weather: ['sunny', 'cloudy', 'rainy', 'stormy', 'foggy', 'snowy', 'misty'],
  
  // Materials & Textures
  texture: ['smooth', 'rough', 'glossy', 'matte', 'metallic', 'organic', 'crystalline'],
  materials: ['glass and steel', 'wood and stone', 'concrete', 'marble', 'copper', 'titanium'],
  
  // Characters & Subjects
  subject: ['young woman', 'elderly man', 'child', 'warrior', 'robot', 'alien creature'],
  character: ['hero', 'villain', 'explorer', 'scientist', 'mythical being', 'cyborg'],
  character_type: ['fantasy warrior', 'sci-fi soldier', 'medieval knight', 'cyberpunk hacker', 'steampunk inventor'],
  creature: ['dragon', 'phoenix', 'griffin', 'unicorn', 'elemental', 'cosmic entity'],
  
  // Actions & Movement
  action: ['running', 'jumping', 'fighting', 'dancing', 'flying', 'exploring'],
  pose: ['standing', 'action pose', 'sitting', 'flying', 'combat stance', 'relaxed'],
  
  // Architecture & Buildings
  building_type: ['skyscraper', 'castle', 'temple', 'cottage', 'palace', 'futuristic tower'],
  architectural_style: ['modern', 'gothic', 'art deco', 'brutalist', 'organic', 'parametric'],
  
  // Product & Angles
  angle: ['45 degree', 'top down', 'front view', 'three-quarter', 'side profile', 'hero angle'],
  product: ['smartphone', 'watch', 'headphones', 'perfume bottle', 'sneakers', 'jewelry'],
  
  // Video Specific
  duration: ['3', '5', '10', '15', '30', '60'],
  speed: ['2', '4', '8', '16', '32', '100'],
  transition_type: ['fade', 'dissolve', 'wipe', 'morph', 'glitch', 'smooth'],
  
  // Quality & Detail
  detail_level: ['minimal', 'moderate', 'high', 'ultra detailed', 'hyper realistic', 'intricate'],
  
  // Aspect Ratios
  aspect_ratio: ['1:1', '4:3', '3:2', '16:9', '2:3', '9:16', '21:9'],
  
  // Additional Creative Elements
  magic_type: ['fire', 'ice', 'lightning', 'nature', 'cosmic', 'shadow', 'light'],
  shapes: ['geometric', 'organic', 'fractal', 'flowing', 'angular', 'spiral'],
  clothing: ['armor', 'robes', 'modern casual', 'formal suit', 'fantasy costume', 'futuristic gear'],
  stylize: ['100', '250', '500', '750', '1000'],
  
  // New v7 specific presets
  camera: ['Canon EOS R5', 'Sony A7R IV', 'Hasselblad X2D', 'ARRI Alexa', 'RED Dragon', 'iPhone 15 Pro'],
  lens: ['85mm f/1.4', '24-70mm f/2.8', '50mm f/1.2', '14mm wide angle', '200mm telephoto', '100mm macro'],
  artist: ['Greg Rutkowski', 'Artgerm', 'WLOP', 'James Gurney', 'Makoto Shinkai', 'Studio Ghibli'],
  technique: ['oil painting', 'digital painting', 'watercolor', 'impasto', 'glazing', 'mixed media'],
  cinematography: ['Roger Deakins style', 'Emmanuel Lubezki style', 'Hoyte van Hoytema style', 'Janusz Kamiński style'],
  color_grading: ['teal and orange', 'bleach bypass', 'day for night', 'high contrast', 'desaturated', 'vibrant'],
  director: ['Christopher Nolan', 'Denis Villeneuve', 'Wes Anderson', 'Ridley Scott', 'David Fincher'],
  genre: ['sci-fi', 'noir', 'western', 'horror', 'fantasy epic', 'cyberpunk thriller'],
  chaos: ['0', '10', '25', '50', '75', '100'],
  weird: ['0', '100', '250', '500', '750', '1000'],
  fps: ['24', '30', '60', '120', '240'],
  quality_tags: ['masterpiece, best quality', '8k uhd, ultra detailed', 'award winning', 'trending on artstation'],
  negative: ['blurry, low quality', 'watermark, signature', 'bad anatomy', 'duplicate, extra limbs'],
  composition: ['rule of thirds', 'golden ratio', 'symmetrical', 'dynamic angle', 'leading lines', 'frame within frame'],
  elements: ['volumetric fog', 'ray tracing', 'particle effects', 'depth of field', 'motion blur', 'lens flare'],
  scene: ['establishing shot', 'action sequence', 'emotional close-up', 'wide landscape', 'interior dialogue', 'chase scene'],
  
  // New video-specific presets
  motion_value: ['60', '120', '180', '240', '360'],
  animation_style: ['2D animation', '3D CGI', 'stop motion', 'hand-drawn', 'motion graphics', 'anime style'],
  camera_angle: ['eye level', 'low angle', 'high angle', 'dutch tilt', 'birds eye', 'worms eye'],
  pacing: ['slow', 'medium', 'fast', 'dynamic', 'rhythmic', 'accelerating'],
  editing_style: ['quick cuts', 'long takes', 'match cuts', 'cross cutting', 'montage', 'jump cuts'],
  visual_theme: ['abstract', 'surreal', 'minimalist', 'maximalist', 'retro', 'futuristic'],
  effect_type: ['explosion', 'magic', 'particle', 'transformation', 'disintegration', 'energy blast'],
  integration: ['seamless', 'stylized', 'realistic', 'fantastical', 'subtle', 'dramatic'],
  particle_effects: ['sparks', 'smoke', 'fire', 'water', 'dust', 'energy orbs'],
  scene_description: ['bustling city street', 'serene nature landscape', 'futuristic sci-fi environment', 'cozy interior', 'dramatic action scene'],
  narrative: ['hero journey', 'love story', 'mystery thriller', 'coming of age', 'redemption arc', 'adventure quest'],
  features: ['key features', 'unique design', 'functionality', 'materials and craftsmanship', 'user benefits'],
  visual_style: ['photorealistic', 'stylized', 'painterly', 'cinematic', 'documentary', 'artistic']
};